import * as Sentry from '@sentry/react';

import type { Breadcrumb, ErrorEvent } from '@sentry/react';

// Error reporting to Sentry (org sharepass, EU region). Errors only: no tracing, no session replay,
// no request or response bodies, no console breadcrumbs.
//
// This page matters more than most: the key that decrypts a secret travels in the URL's fragment and
// never reaches a server. It must not reach Sentry either, so every URL is cut back to its path
// before anything is sent, in the event, its breadcrumbs and its message.

const URL_IN_TEXT = /\b(https?:\/\/[^\s"'<>?#]*)[?#][^\s"'<>]*/g;

/** A URL without its query and fragment: `https://reveal.sharepass.com/secret/a#/b/key` -> `.../secret/a`. */
export const stripUrl = (url: string): string => url.split(/[?#]/, 1)[0];

/** The same, for every absolute URL inside free text such as an error message. */
export const stripUrlsInText = (text: string): string => text.replace(URL_IN_TEXT, '$1');

const stripField = (data: Record<string, unknown> | undefined, key: string) => {
    const value = data?.[key];
    if (data && typeof value === 'string') data[key] = stripUrl(value);
};

export const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
    if (breadcrumb.category === 'console') return null;
    stripField(breadcrumb.data, 'url');
    stripField(breadcrumb.data, 'from');
    stripField(breadcrumb.data, 'to');
    if (breadcrumb.message) breadcrumb.message = stripUrlsInText(breadcrumb.message);
    return breadcrumb;
};

export const scrubEvent = (event: ErrorEvent): ErrorEvent => {
    if (event.request) {
        if (event.request.url) event.request.url = stripUrl(event.request.url);
        delete event.request.query_string;
        delete event.request.cookies;
        delete event.request.data;
        const headers = event.request.headers;
        if (headers) {
            for (const name of Object.keys(headers)) {
                if (/^referr?er$/i.test(name)) headers[name] = stripUrl(headers[name]);
                else if (!/^user-agent$/i.test(name)) delete headers[name];
            }
        }
    }
    if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
        delete event.user.username;
    }
    if (event.message) event.message = stripUrlsInText(event.message);
    for (const exception of event.exception?.values ?? []) {
        if (exception.value) exception.value = stripUrlsInText(exception.value);
    }
    event.breadcrumbs = event.breadcrumbs?.map(scrubBreadcrumb).filter((b): b is Breadcrumb => b !== null);
    return event;
};

/**
 * Answers this page handles itself: a wrong PIN, a link already opened, one that expired. They are
 * the product working, and reporting them would bury the ones that matter.
 */
const EXPECTED_STATUSES = new Set([401, 403, 404, 409, 410, 423, 429]);

/**
 * A request that failed in a way nobody planned for. Whoever opens a reveal link is not a customer
 * and will not report anything: they simply do not get their secret and leave, so this is the only
 * way such a failure is ever heard about.
 */
export const reportApiFailure = (details: { endpoint: string; status: number | string; message?: string }): void => {
    const { endpoint, status, message } = details;

    if (typeof status === 'number' && EXPECTED_STATUSES.has(status)) return;

    const path = stripUrl(endpoint);

    Sentry.captureMessage(`API ${status} ${path}`, {
        level: typeof status === 'number' && status >= 500 ? 'error' : 'warning',
        tags: { api_status: String(status), api_endpoint: path },
        ...(message ? { extra: { message: stripUrlsInText(message) } } : {}),
    });
};

/** The page gave up and showed its generic error: someone is holding a link that does not open. */
export const reportDeadEnd = (where: string, reason?: string): void => {
    Sentry.captureMessage(`Dead end: ${where}`, {
        level: 'warning',
        tags: { dead_end: where },
        ...(reason ? { extra: { reason: stripUrlsInText(reason) } } : {}),
    });
};

/** Starts error reporting on production builds that carry a DSN; does nothing anywhere else. */
export const initMonitoring = (): void => {
    const dsn = import.meta.env.VITE_SENTRY_DSN;
    if (!import.meta.env.PROD || !dsn) return;

    Sentry.init({
        dsn,
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'reveal',
        release: import.meta.env.VITE_RELEASE || undefined,
        sendDefaultPii: false,
        maxBreadcrumbs: 50,
        integrations: [Sentry.breadcrumbsIntegration({ console: false })],
        beforeBreadcrumb: scrubBreadcrumb,
        beforeSend: scrubEvent,
    });
};
